import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { Segment, SegmentSetOperationResponse } from '../../../models/segment.model';
import { SegmentService } from '../../../services/segment-api.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { ModalComponent } from '../../../../../shared/components/ui/modal/modal.component';

@Component({
  selector: 'app-segment-set-ops-section',
  standalone: true,
  imports: [CommonModule, ModalComponent],
  templateUrl: './segment-set-ops-section.component.html',
})
export class SegmentSetOpsSectionComponent implements OnChanges, OnDestroy {
  @Input() segmentId: number = 0;
  @Input() showButton: boolean = true;
  @Input() showContent: boolean = true;
  @Output() executed = new EventEmitter<void>();

  existingSetOps: SegmentSetOperationResponse[] = [];
  isModalOpen = false;
  setOpType: 'UNION' | 'INTERSECT' | 'DIFFERENCE' = 'UNION';
  availableSegments: Segment[] = [];
  selectedSourceIds: number[] = [];
  baseSegmentId: number | null = null;
  subtractIds: number[] = [];
  isExecuting = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private segmentApi: SegmentService,
    private toast: ToastService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['segmentId'] && this.segmentId > 0) {
      this.cargarSetOperations();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargarSetOperations(): void {
    this.segmentApi.getSetOperations(this.segmentId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.existingSetOps = res || []; },
      error: () => {
        this.existingSetOps = [];
        this.toast.error({ title: 'Error', message: 'No se pudieron cargar las operaciones de conjuntos.' });
      },
    });
  }

  openModal(): void {
    this.setOpType = 'UNION';
    this.selectedSourceIds = [];
    this.baseSegmentId = null;
    this.subtractIds = [];
    this.isExecuting = false;
    this.isModalOpen = true;

    this.segmentApi.getSegments(0, 100, { status: 'READY' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.availableSegments = (res.content || []).filter(s => s.id !== this.segmentId);
        },
        error: () => {
          this.availableSegments = [];
          this.toast.error({ title: 'Error', message: 'No se pudieron cargar los segmentos disponibles.' });
        },
      });
  }

  closeModal(): void {
    this.isModalOpen = false;
  }

  onOpTypeChange(type: 'UNION' | 'INTERSECT' | 'DIFFERENCE'): void {
    this.setOpType = type;
    this.selectedSourceIds = [];
    this.baseSegmentId = null;
    this.subtractIds = [];
  }

  toggleSource(id: number): void {
    const idx = this.selectedSourceIds.indexOf(id);
    if (idx === -1) this.selectedSourceIds.push(id);
    else this.selectedSourceIds.splice(idx, 1);
  }

  toggleBase(id: number): void {
    this.baseSegmentId = this.baseSegmentId === id ? null : id;
    this.subtractIds = this.subtractIds.filter(s => s !== id);
  }

  toggleSubtract(id: number): void {
    const idx = this.subtractIds.indexOf(id);
    if (idx === -1) this.subtractIds.push(id);
    else this.subtractIds.splice(idx, 1);
  }

  isSelected(id: number): boolean {
    return this.selectedSourceIds.includes(id);
  }

  get canExecute(): boolean {
    if (this.setOpType === 'DIFFERENCE') return !!this.baseSegmentId && this.subtractIds.length > 0;
    return this.selectedSourceIds.length > 0;
  }

  ejecutar(): void {
    if (!this.canExecute) return;
    const sourceSegmentIds = this.setOpType === 'DIFFERENCE'
      ? [this.baseSegmentId!, ...this.subtractIds]
      : this.selectedSourceIds;
    this.isExecuting = true;

    this.segmentApi.executeSetOperation(this.segmentId, {
      sourceSegmentIds,
      operationType: this.setOpType,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.isExecuting = false;
        this.closeModal();
        this.cargarSetOperations();
        this.executed.emit();
      },
      error: () => {
        this.isExecuting = false;
        this.toast.error({ title: 'Error', message: 'No se pudo ejecutar la operación de conjuntos.' });
      },
    });
  }

  getOpLabel(type: string): string {
    const labels: Record<string, string> = { UNION: 'Unión', INTERSECT: 'Intersección', DIFFERENCE: 'Diferencia' };
    return labels[type] ?? type;
  }
}
